import { League } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PatternBackground } from '@/src/presentation/components';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { ActivityIndicator, FlatList, Image, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './ExploreLeaguesScreen.styles';

export interface ExploreLeaguesScreenProps {
  leagues: League[];
  loading: boolean;
  loadingMore: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  onLeaguePress: (leagueId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: any;
}

export const ExploreLeaguesScreen = ({
  leagues,
  loading,
  loadingMore,
  searchQuery,
  setSearchQuery,
  onLeaguePress,
  onRefresh,
  onLoadMore,
  colors,
}: ExploreLeaguesScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const filtered = searchQuery.trim()
    ? leagues.filter((l) => l.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : leagues;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return colors.badgeCompleted;
      case 'active': return colors.badgeProgress;
      default: return colors.badgeSetup;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'setup': return t('leagues.setup');
      case 'active': return t('leagues.active');
      case 'completed': return t('leagues.completed');
      default: return status;
    }
  };

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={[styles.searchContainer, { backgroundColor: colors.background, borderColor: colors.border }]}>
          <Ionicons name="search" size={20} color={colors.textTertiary} style={{ marginRight: 8 }} />
          <TextInput
            style={[styles.searchInput, { color: colors.text }]}
            placeholder={t('leagues.discoverLeagues')}
            placeholderTextColor={colors.textTertiary}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.leagueCard, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={() => onLeaguePress(item.id)}
              activeOpacity={0.7}
            >
              <View style={styles.leagueHeader}>
                <View style={[styles.logoCircle, { backgroundColor: colors.background, borderColor: colors.border }]}>
                  {item.logoUrl ? (
                    <Image source={{ uri: item.logoUrl }} style={styles.logoImage} />
                  ) : (
                    <MaterialCommunityIcons name="trophy-outline" size={18} color={colors.textTertiary} />
                  )}
                </View>
                <Text style={[styles.leagueName, { color: colors.text }]} numberOfLines={1}>
                  {item.name}
                </Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                  <Text style={styles.statusText}>{getStatusLabel(item.status)}</Text>
                </View>
              </View>
              {item.description ? (
                <Text style={[styles.leagueDesc, { color: colors.textSecondary }]} numberOfLines={2}>
                  {item.description}
                </Text>
              ) : null}
              <View style={styles.leagueFooter}>
                <Ionicons name="people-outline" size={14} color={colors.textTertiary} />
                <Text style={[styles.footerText, { color: colors.textTertiary }]}>
                  {item.pointSystem?.length ?? 0} {t('leagues.pointSystem').toLowerCase()}
                </Text>
                <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
              </View>
            </TouchableOpacity>
          )}
          contentContainerStyle={filtered.length === 0 ? styles.emptyList : styles.listContent}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.3}
          onRefresh={onRefresh}
          refreshing={loading}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Ionicons name="trophy-outline" size={52} color={colors.textTertiary} style={{ marginBottom: 16 }} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                {t('leagues.noPublicLeagues')}
              </Text>
              <Text style={[styles.emptySub, { color: colors.textTertiary }]}>
                {t('leagues.discoverLeagues')}
              </Text>
            </View>
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={styles.footer}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
        />
      )}
    </PatternBackground>
  );
};
